import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, errMsg, money } from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";

const LABEL: any = { afterpay: "Afterpay", zip: "Zip" };

function useConfirm() {
  const nav = useNavigate();
  const [err, setErr] = useState("");
  const run = async (provider: string, token: string, approved: boolean) => {
    try { await api.post(`/instalments/${provider}/confirm`, { token, approved }); nav(`/payment/success?session_id=${encodeURIComponent(token)}`); }
    catch (e) { setErr(errMsg(e)); }
  };
  return { err, run };
}

const Failed = ({ msg }: { msg: string }) => (
  <div className="max-w-xl mx-auto text-center px-5 py-28" data-testid="instalment-failed">
    <h1 className="font-display text-4xl font-light">Payment not completed</h1>
    <p className="mt-5 text-neutral-600" data-testid="instalment-failed-message">{msg}</p>
    <Link to="/checkout" className="btn-ink inline-block mt-10 px-8 py-4 text-xs tracking-[0.25em] uppercase" data-testid="instalment-back-checkout">Return to Checkout</Link>
  </div>
);

export function InstalmentTest() {
  useMeta("Test instalment payment");
  const [q] = useSearchParams();
  const provider = q.get("provider") || "", token = q.get("token") || "";
  const [o, setO] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const { err, run } = useConfirm();
  useEffect(() => { api.get(`/instalments/test/${encodeURIComponent(token)}`).then((r) => setO(r.data)).catch(() => setO(false)); }, [token]);
  if (err) return <Failed msg={err} />;
  if (o === false) return <Failed msg="This test checkout could not be found." />;
  if (!o) return <div className="py-28 flex justify-center"><Loader2 className="animate-spin" /></div>;
  const each = Math.round((o.total / 4) * 100) / 100;
  const act = (ok: boolean) => { setBusy(true); run(provider, token, ok); };
  return (
    <div className="max-w-md mx-auto px-5 py-20" data-testid="instalment-test-page">
      <p className="eyebrow text-amber-700 mb-3" data-testid="instalment-test-banner">Test mode — no real money moves</p>
      <h1 className="font-display text-4xl font-light">{LABEL[provider]} checkout</h1>
      <div className="border border-neutral-200 bg-white p-6 mt-8 space-y-3 text-sm">
        <p className="flex justify-between"><span>Order</span><span data-testid="instalment-test-order">{o.number}</span></p>
        <p className="flex justify-between"><span>Total</span><span data-testid="instalment-test-total">{money(o.total)}</span></p>
        <div className="grid grid-cols-4 gap-2 pt-3 border-t border-neutral-100">{[0, 1, 2, 3].map((i) => <div key={i} className="text-center"><p className="text-xs text-neutral-500">{i ? `In ${i * 2} weeks` : "Today"}</p><p className="font-medium" data-testid={`instalment-test-payment-${i}`}>{money(each)}</p></div>)}</div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-8">
        <button disabled={busy} onClick={() => act(false)} className="btn-line py-4 text-xs tracking-[0.2em] uppercase disabled:opacity-50" data-testid="instalment-test-decline">Decline</button>
        <button disabled={busy} onClick={() => act(true)} className="btn-ink py-4 text-xs tracking-[0.2em] uppercase disabled:opacity-50" data-testid="instalment-test-approve">Approve</button>
      </div>
    </div>
  );
}

export function InstalmentReturn() {
  useMeta("Confirming your payment");
  const [q] = useSearchParams();
  const provider = q.get("provider") || "";
  const token = q.get("orderToken") || q.get("checkoutId") || "";
  const ok = (q.get("status") || q.get("result") || "").toLowerCase();
  const { err, run } = useConfirm();
  useEffect(() => { if (token) run(provider, token, ok === "success" || ok === "approved"); }, []);
  if (!token) return <Failed msg="We couldn't find your payment details." />;
  if (err) return <Failed msg={err} />;
  return <div className="max-w-xl mx-auto text-center px-5 py-28" data-testid="instalment-return"><Loader2 className="animate-spin mx-auto mb-6" /><p className="font-display text-3xl">Confirming your {LABEL[provider] || ""} payment…</p></div>;
}
